import { useEffect, useRef } from 'react';
import { useDarkMode } from '../context/DarkModeContext';

const CANDLE_W = 12;
const GAP      = 6;
const STEP     = CANDLE_W + GAP;
const SPEED    = 0.4; // px per frame (~24px/s at 60fps)

function makeCandle(prevClose) {
  const drift  = (Math.random() - 0.47) * 0.028;
  const open   = prevClose;
  const close  = open * (1 + drift);
  const span   = Math.abs(close - open);
  const high   = Math.max(open, close) + Math.random() * span * 0.8 + prevClose * 0.003;
  const low    = Math.min(open, close) - Math.random() * span * 0.8 - prevClose * 0.003;
  return { open, high, low, close };
}

function seedCandles(n = 120) {
  const arr = [];
  let price = 100;
  for (let i = 0; i < n; i++) {
    const c = makeCandle(price);
    arr.push(c);
    price = c.close;
  }
  return arr;
}

export default function HeroChartBg() {
  const canvasRef = useRef(null);
  const { dark } = useDarkMode();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx  = canvas.getContext('2d');
    let raf;
    let scrollX  = 0;
    let lastW    = 0;
    let lastH    = 0;
    const candles = seedCandles();

    function ensureCandles(upToIdx) {
      while (candles.length <= upToIdx + 10) {
        candles.push(makeCandle(candles[candles.length - 1].close));
      }
    }

    function draw() {
      const dpr = window.devicePixelRatio || 1;
      const el  = canvas.parentElement;
      if (!el) { raf = requestAnimationFrame(draw); return; }
      const W = el.offsetWidth;
      const H = el.offsetHeight;

      // Resize canvas only when dimensions change (resetting transform)
      if (W !== lastW || H !== lastH) {
        canvas.width  = W * dpr;
        canvas.height = H * dpr;
        ctx.scale(dpr, dpr);
        lastW = W; lastH = H;
      }

      ctx.clearRect(0, 0, W, H);

      const startIdx = Math.max(0, Math.floor(scrollX / STEP) - 1);
      const endIdx   = startIdx + Math.ceil(W / STEP) + 3;
      ensureCandles(endIdx);

      const slice = candles.slice(startIdx, endIdx + 1);

      // Y scale — fit slice prices with padding
      const allPrices = slice.flatMap(c => [c.high, c.low]);
      const minP  = Math.min(...allPrices);
      const maxP  = Math.max(...allPrices);
      const range = maxP - minP || 1;
      const pad   = range * 0.3;
      const toY   = p => H * 0.08 + (1 - (p - minP + pad) / (range + pad * 2)) * H * 0.84;

      // ── Single grey — globalAlpha keeps fill/stroke/wick visually identical
      const grey = dark ? '#b4b4b4' : '#646464';
      ctx.globalAlpha = dark ? 0.22 : 0.16;
      ctx.fillStyle   = grey;
      ctx.strokeStyle = grey;

      // ── Candlesticks ─────────────────────────────────────────────────────
      slice.forEach((c, si) => {
        const x  = (startIdx + si) * STEP - scrollX;
        const cx = x + CANDLE_W / 2;
        const isUp = c.close >= c.open;

        const y1    = toY(Math.max(c.open, c.close)); // body top
        const y2    = toY(Math.min(c.open, c.close)); // body bottom
        const bodyH = Math.max(1.5, y2 - y1);
        const highY = toY(c.high);
        const lowY  = toY(c.low);

        ctx.lineWidth = 1;

        // Upper wick — high to body top only (no line inside body)
        ctx.beginPath();
        ctx.moveTo(cx, highY);
        ctx.lineTo(cx, y1);
        ctx.stroke();

        // Lower wick — body bottom to low only
        ctx.beginPath();
        ctx.moveTo(cx, y2);
        ctx.lineTo(cx, lowY);
        ctx.stroke();

        if (isUp) {
          // Solid body — drawn after wicks so it sits cleanly on top
          ctx.fillRect(x, y1, CANDLE_W, bodyH);
        } else {
          // Clear interior so nothing bleeds through, then draw outline
          ctx.clearRect(x + 1, y1 + 1, CANDLE_W - 2, bodyH - 2);
          ctx.strokeRect(x + 0.5, y1 + 0.5, CANDLE_W - 1, bodyH - 1);
        }
      });

      ctx.globalAlpha = 1;

      scrollX += SPEED;
      raf = requestAnimationFrame(draw);
    }

    draw();
    return () => cancelAnimationFrame(raf);
  }, [dark]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{
        maskImage: 'linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 18%, black 82%, transparent 100%)',
      }}
    />
  );
}
